import type { Model, ModelCapabilities } from "@lumen/shared";

export type Metadata = {
  canonical_slug?: string;
  context_length?: number;
  knowledge_cutoff?: string | null;
  architecture?: {
    modality?: string;
    input_modalities?: string[];
    output_modalities?: string[];
    tokenizer?: string;
  };
  pricing?: Record<string, string>;
  top_provider?: { context_length?: number; max_completion_tokens?: number };
  supported_parameters?: string[];
};

export type RichModel = Model & {
  providerName?: string;
  providerEnabled?: boolean;
  toolsEnabled?: boolean;
  sortOrder?: number;
  scope?: "platform" | "organization" | "personal";
};

export const metadataFor = (model: RichModel): Metadata => ({
  ...(model.remoteMetadata as Metadata),
  canonical_slug: model.canonicalSlug ?? undefined,
  context_length: model.contextLength ?? undefined,
  architecture: (model.architecture as Metadata["architecture"]) ?? undefined,
  pricing: (model.pricing as Record<string, string>) ?? undefined,
  supported_parameters: model.supportedParameters,
  top_provider:
    (model.providerMetadata as Metadata["top_provider"]) ?? undefined,
});

export const inferred = (model: RichModel): ModelCapabilities => {
  const metadata = metadataFor(model);
  const inputs = metadata.architecture?.input_modalities ?? [];
  const outputs = metadata.architecture?.output_modalities ?? [];
  const parameters = metadata.supported_parameters ?? [];
  return {
    vision: model.capabilities.vision || inputs.includes("image"),
    imageGeneration:
      model.capabilities.imageGeneration || outputs.includes("image"),
    tools:
      model.capabilities.tools ||
      parameters.some((value) => value === "tools" || value === "tool_choice"),
  };
};

export const formatNumber = (value?: number) =>
  value ? new Intl.NumberFormat().format(value) : "—";
